import { List, Map } from 'immutable';
import getIn from 'lodash/get';
import isFunction from 'lodash/isFunction';

import * as Constants from '../constants';
import { getInDomain } from '../domains/externalUtils';
import { getAnnotationsById, getDetails, getTrackById } from '../lib/EntityUtils';
import { getPandoraIdFromMusicId } from '../lib/PandoraIdUtil';
import {
  getLyricSnippet,
  getLyricSnippetAPS,
  isAudioWarning,
  isTrackThumbedDown,
  isTrackThumbedUp,
  shouldTrackPersistInSessionHistory,
} from '../lib/PlaylistTrackUtils';
import { getTrackAlbumPath, getTrackArtistPath, getTrackSongPath } from '../lib/RouteUtils';
import { isStationVoiceTrack } from '../lib/VoiceTrackUtils';

const modelResolverKey = 'MODEL_RESOLVER_WEB_APP';
// TODO: this should probably become something we construct at runtime
const MODEL_MAP = {
  APSItem: {
    [modelResolverKey]: (state: $TSFixMe, pandoraId: string): $TSFixMe => {
      const item = getInDomain('playback.selectors.getSessionItem', Map())(state, pandoraId);
      const annotation = getAnnotationsById(state, pandoraId);
      let track = getInDomain('playback.selectors.getCurrentItemAudioTrack', Map())(state);

      track = track?.set('sessionItem', item);

      return annotation ? annotation.merge(track) : track;
    },
    type: (track: $TSFixMe): $TSFixMe => {
      return track.getIn(['sessionItem', 'type']);
    },
    sessionTrackToken: (track: $TSFixMe): $TSFixMe => {
      return track.getIn(['sessionItem', 'pandoraId']);
    },
    trackToken: 'pandoraId',
    name: 'name',
    shareableUrlPath: 'shareableUrlPath',
    lyricSnippet: (_track: $TSFixMe, state: $TSFixMe): $TSFixMe => {
      const lyricSnippet = getLyricSnippetAPS(state);
      return lyricSnippet && List(lyricSnippet.split('\n'));
    },
    artist: (track: $TSFixMe, state: $TSFixMe): $TSFixMe => {
      const artistId = track.get('artistId');
      const annotation = getAnnotationsById(state, artistId);
      return annotation && annotation.merge(getDetails(state, artistId));
    },
    artistName: (track: $TSFixMe, state: $TSFixMe): $TSFixMe => {
      return getInDomain('catalog.utils.getTrackAuthor')(state, track);
    },
    artistPandoraId: 'artistId',
    artistPath: (track: $TSFixMe, state: $TSFixMe): $TSFixMe => {
      const annotation = getAnnotationsById(state, track.get('artistId'));
      return annotation && annotation.get('shareableUrlPath');
    },
    albumTitle: (track: $TSFixMe, state: $TSFixMe): $TSFixMe => {
      const annotation = getAnnotationsById(state, track.get('albumId'));
      return annotation && annotation.get('name');
    },
    albumPath: (track: $TSFixMe, state: $TSFixMe): $TSFixMe => {
      const annotation = getAnnotationsById(state, track.get('albumId'));
      return annotation && annotation.get('shareableUrlPath');
    },
    shouldPersistInSessionHistory: (track: $TSFixMe): boolean => {
      return shouldTrackPersistInSessionHistory(track);
    },
    shouldShowReplay: (): true => {
      return true;
    },
    shouldShowThumbs: (): true => {
      return true; // this will need to take track type into account before turning APS on for stations
    },
    isReplayEnabled: (track: $TSFixMe): boolean => {
      return track
        .getIn(['sessionItem', 'interactions'], List())
        .includes(Constants.Playback.PLAYBACK_CONTROLS.REPLAY);
    },
    isFeedbackEnabled: (track: $TSFixMe): boolean => {
      return track
        .getIn(['sessionItem', 'interactions'], List())
        .includes(Constants.Playback.PLAYBACK_CONTROLS.THUMBS);
    },
    isVoiceTrack: (track: $TSFixMe): boolean => {
      return getInDomain('catalog.utils.isCatalogVoiceTrack')(track);
    },
    trackThumbedDown: (track: $TSFixMe): boolean => {
      return track.getIn(['sessionItem', 'rating'], 0) === -1;
    },
    trackThumbedUp: (track: $TSFixMe): boolean => {
      return track.getIn(['sessionItem', 'rating'], 0) === 1;
    },
  },
  PlaylistTrack: {
    [modelResolverKey]: getTrackById,
    type: 'trackType',
    trackToken: 'trackToken',
    name: (track: $TSFixMe, state: $TSFixMe): string => {
      if (isStationVoiceTrack(track)) {
        const trackAnnotation = getAnnotationsById(state, track.get('pandoraId'));
        return trackAnnotation && trackAnnotation.get('name');
      }
      return track.get('songTitle');
    },
    shareableUrlPath: (track: $TSFixMe): string | boolean => {
      return !isAudioWarning(track) && getTrackSongPath(track);
    },
    lyricSnippet: (track: $TSFixMe, state: $TSFixMe): $TSFixMe => {
      const lyricData = getLyricSnippet(track, state);
      return lyricData && lyricData.get('lines');
    },
    artist: (track: $TSFixMe, state: $TSFixMe): $TSFixMe => {
      // eslint-disable-next-line no-use-before-define
      const artistId = getFromModel(track, 'PlaylistTrack', 'artistPandoraId', state);
      const annotation = getAnnotationsById(state, artistId) || Map();
      const detail =
        !isStationVoiceTrack(track) ||
        annotation.get('type') === Constants.TrackType.VoiceTrackAuthorType.ARTIST
          ? getDetails(state, artistId)
          : Map();
      return annotation && annotation.merge(detail);
    },
    artistName: (track: $TSFixMe, state: $TSFixMe): $TSFixMe => {
      if (isStationVoiceTrack(track)) {
        const attributes = getInDomain('playback.utils.getVoiceTrackAttributes')(state, track);
        return (attributes && attributes.artistName) || '';
      }
      return track.get('artistName');
    },
    artistPandoraId: (track: $TSFixMe, state: $TSFixMe): $TSFixMe => {
      let artistId;
      if (isStationVoiceTrack(track)) {
        const trackAnnotation = getAnnotationsById(state, track.get('pandoraId'));
        artistId = trackAnnotation && trackAnnotation.get('authorId');
      } else if (track.get('artistMusicId')) {
        artistId = getPandoraIdFromMusicId(track.get('artistMusicId'));
      }
      return artistId;
    },
    artistPath: (track: $TSFixMe): $TSFixMe => {
      return !isAudioWarning(track) && getTrackArtistPath(track);
    },
    albumTitle: (track: $TSFixMe): $TSFixMe => {
      return !isAudioWarning(track) && track.get('albumTitle');
    },
    albumPath: (track: $TSFixMe): $TSFixMe => {
      return !isAudioWarning(track) && getTrackAlbumPath(track);
    },
    shouldPersistInSessionHistory: (track: $TSFixMe): $TSFixMe => {
      return shouldTrackPersistInSessionHistory(track);
    },
    shouldShowReplay: (track: $TSFixMe, state: $TSFixMe): $TSFixMe => {
      return getInDomain('playback.utils.shouldShowReplayControl')(state, track);
    },
    isReplayEnabled: (track: $TSFixMe, state: $TSFixMe): $TSFixMe => {
      return getInDomain('playback.utils.getEnabledPlaybackControls')(state, track).contains(
        Constants.Playback.PLAYBACK_CONTROLS.REPLAY,
      );
    },
    shouldShowThumbs: (track: $TSFixMe): $TSFixMe => {
      return !isStationVoiceTrack(track);
    },
    isFeedbackEnabled: (track: $TSFixMe, state: $TSFixMe): $TSFixMe => {
      return getInDomain('playback.utils.allowFeedbackForTrack')(state, track);
    },
    isVoiceTrack: (track: $TSFixMe): boolean => {
      return isStationVoiceTrack(track);
    },
    trackThumbedDown: (track: $TSFixMe): boolean => {
      return isTrackThumbedDown(track);
    },
    trackThumbedUp: (track: $TSFixMe): boolean => {
      return isTrackThumbedUp(track);
    },
  },
};

export function resolveModelFromKey(
  state: $TSFixMe,
  key: $TSFixMe,
  type: $TSFixMe,
): null | $TSFixMe {
  const resolver = getIn(MODEL_MAP, `${type}.${modelResolverKey}`);
  if (!isFunction(resolver)) {
    // Error
    return null;
  }
  return resolver(state, key);
}

export function getFromModel(
  model: $TSFixMe,
  type: $TSFixMe,
  attr: $TSFixMe,
  state: $TSFixMe,
): null | $TSFixMe {
  if (!Map.isMap(model)) {
    // Error, model must be a map
    return null;
  }
  const value = getIn(MODEL_MAP, `${type}.${attr}`);
  if (value) {
    if (isFunction(value)) return value(model, state);
    return model.get(value, null);
  }
  return model.get(attr, null);
}

export function getFirstModel(
  types: $TSFixMe[] = [],
  state: $TSFixMe,
  lookupKey: $TSFixMe,
): null | $TSFixMe {
  if (!lookupKey) return null;
  let result;
  for (let i = 0; i < types.length; i++) {
    result = resolveModelFromKey(state, lookupKey, types[i]);
    if (result) return result;
  }
  return null;
}
